import {
  MessagesApplicationsApplication,
  MessagesSubscriptionsOrganisationTeam,
  MessagesSubscriptionsOrganisation,
  MessagesSubscriptionsNewOrganisationTeam,
} from '@/dotnet-client'
import { Field, Input, Select } from '@fluentui/react-components'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { createRestClient } from '@/contexts/QueryProvider'
import { Button } from '../Button'
import './Form.scss'
import { ErrorComponent } from '../ErrorComponent'
import { Loading } from '../Loading'
import { useGetSearchParam } from '@/hooks'
import { useEffect } from 'react'

type OrganisationTeamFormProps = {
  organisationId: number
  onSubmit: () => void
  selectedTeam?: MessagesSubscriptionsOrganisationTeam
}

type TSubscriptionsOrganisationTeamForm = {
  id?: number
  applicationId: number
  name: string
  description: string
  application: string
  selectedTeam?: MessagesSubscriptionsOrganisationTeam
}

export const OrganisationTeamForm = (props: OrganisationTeamFormProps) => {
  const queryClient = useQueryClient()
  const organisationId = props.organisationId
  const organisationName = useGetSearchParam('orgName')

  const {
    getApplicationsApiGetApplications,
    getSubscriptionsApiGetOrganisationTeams, // invalidate this when adding/updating
    getSubscriptionsApiGetOrganisations, // invalidate this when adding/updating
    postSubscriptionsApiUpdateTeam,
    putSubscriptionsApiAddTeam,
  } = createRestClient()

  const {
    data: applications,
    isPending: isAppsPending,
    isError: isAppsError,
  } = getApplicationsApiGetApplications.useQuery<MessagesApplicationsApplication[]>()

  const {
    data: _organisations,
    isError: isOrganisationsError,
    isPending: isOrganisationsLoading,
  } = getSubscriptionsApiGetOrganisations.useQuery<MessagesSubscriptionsOrganisation[]>()

  const {
    data: _organisationTeams,
    isError: isOrgTeamsError,
    isPending: isOrgTeamsLoading,
  } = getSubscriptionsApiGetOrganisationTeams.useQuery<MessagesSubscriptionsOrganisationTeam[]>({
    query: { orgId: organisationId || 0 },
  })

  const { mutate: addTeam, isError: isAddTeamError, isPending: isAddingTeam } = putSubscriptionsApiAddTeam.useMutation()

  const {
    mutate: updateTeam,
    isError: isUpdateTeamError,
    isPending: isUpdatingTeam,
  } = postSubscriptionsApiUpdateTeam.useMutation()

  const invalidateQueries = (organisationId?: number) => {
    getSubscriptionsApiGetOrganisationTeams.invalidateQueries(
      { parameters: { query: { orgId: organisationId || 0 } } },

      queryClient
    )

    getSubscriptionsApiGetOrganisations.invalidateQueries({}, queryClient)
  }

  const loading = isUpdatingTeam || isOrganisationsLoading || isAddingTeam || isOrgTeamsLoading || isAppsPending
  const error = isUpdateTeamError || isAddTeamError || isOrganisationsError || isOrgTeamsError || isAppsError

  const {
    register,
    formState: { errors },
    //setError,
    reset,
    handleSubmit,
    // control
  } = useForm<TSubscriptionsOrganisationTeamForm>({
    defaultValues: props.selectedTeam
      ? ({
          organisationId: organisationId ?? props.selectedTeam?.organisationId ?? 0,
          id: props.selectedTeam?.id ?? 0,
          name: props.selectedTeam?.name ?? '',
          description: props.selectedTeam?.description ?? '',
          application: props.selectedTeam?.application ?? '',
          applicationId: props.selectedTeam?.applicationId ?? 0,
        } as TSubscriptionsOrganisationTeamForm)
      : ({
          organisationId: organisationId || 0, // ✅ Correct Fallback for New Orgs
          id: undefined,
          name: '',
          description: '',
          application: '',
          applicationId: 0,
        } as TSubscriptionsOrganisationTeamForm),
  })

  useEffect(() => {
    if (props.selectedTeam) {
      reset({
        applicationId: props.selectedTeam.applicationId ?? 0,
        id: props.selectedTeam.id ?? 0,
        name: props.selectedTeam.name ?? '',
        description: props.selectedTeam.description ?? '',
        application: props.selectedTeam?.application ?? '',
      })
    }
  }, [props.selectedTeam, reset])

  const onSubmit = (data: TSubscriptionsOrganisationTeamForm) => {
    if (!data.applicationId) {
      console.log(error)
      toast.error('Application is required')
      return
    }

    if (props.selectedTeam) {
      const updatedTeam: MessagesSubscriptionsOrganisationTeam = {
        id: props.selectedTeam?.id ?? 0,
        name: data.name ?? props.selectedTeam?.name ?? '',
        description: data.description ?? props.selectedTeam?.description ?? '',
        linkedProjectCount: props.selectedTeam?.linkedProjectCount ?? 0,
        activeLinkedProjectCount: props.selectedTeam?.activeLinkedProjectCount ?? 0,
        linkedUserCount: props.selectedTeam?.linkedUserCount ?? 0,
        linkedRoleCount: props.selectedTeam?.linkedRoleCount ?? 0,
        applicationId: props.selectedTeam?.applicationId ?? 0,
        typeName: props.selectedTeam?.typeName || undefined,
        organisationId: props.selectedTeam?.organisationId ?? organisationId ?? 0,
        organisation: props.selectedTeam?.organisation ?? '',
        application: props.selectedTeam?.application ?? '',
      }

      updateTeam(
        {
          body: {
            id: updatedTeam.id,
            name: updatedTeam.name,
            description: updatedTeam.description,
            applicationId: updatedTeam.applicationId,
            typeName: updatedTeam.typeName,
            organisationId: updatedTeam.organisationId,
            application: updatedTeam.application ?? '',
            organisation: updatedTeam.organisation ?? '',
            linkedProjectCount: updatedTeam.linkedProjectCount,
            activeLinkedProjectCount: updatedTeam.activeLinkedProjectCount,
            linkedUserCount: updatedTeam.linkedUserCount,
            linkedRoleCount: updatedTeam.linkedRoleCount,
          },
        },
        {
          onSuccess: () => {
            invalidateQueries(organisationId)
            props.onSubmit()
            toast.success('Item successfully updated')
            reset()
          },
          onError: () => {
            toast.error('Error updating item')
            console.error(error)
            props.onSubmit()
          },
        }
      )
    } else {
      const newTeam: MessagesSubscriptionsNewOrganisationTeam = {
        ...data,
        id: 0, // Avoid `undefined`
        applicationId: data?.applicationId ?? 0,
        application: data?.application ?? '',
        name: data?.name ?? '',
        description: data?.description ?? '',
        typeName: data.selectedTeam?.typeName ?? undefined, // Provide a sensible default
        linkedProjectCount: data.selectedTeam?.linkedProjectCount ?? 0,
        activeLinkedProjectCount: data.selectedTeam?.activeLinkedProjectCount ?? 0,
        linkedUserCount: data.selectedTeam?.linkedUserCount ?? 0,
        linkedRoleCount: data.selectedTeam?.linkedRoleCount ?? 0,
        organisationId: data.selectedTeam?.organisationId ?? organisationId ?? 0,
        organisation: data.selectedTeam?.organisation ?? organisationName ?? '',
      }

      addTeam(
        {
          body: {
            id: newTeam.id ?? 0,
            name: newTeam.name,
            description: newTeam.description,
            linkedProjectCount: newTeam.linkedProjectCount,
            activeLinkedProjectCount: newTeam.activeLinkedProjectCount,
            linkedUserCount: newTeam.linkedUserCount,
            linkedRoleCount: newTeam.linkedRoleCount,
            applicationId: newTeam.applicationId,
            typeName: newTeam.typeName ?? undefined,
            organisationId: newTeam.organisationId ?? 0,
            application: newTeam.application ?? '',
            organisation: newTeam.organisation ?? '',
          },
        },
        {
          onSuccess: () => {
            invalidateQueries(organisationId)
            props.onSubmit()
            toast.success('Item successfully added')
            reset()
          },
          onError: () => {
            toast.error('Error adding item')
            props.onSubmit()
          },
        }
      )
    }
  }

  if (loading) return <Loading />
  if (error) return <ErrorComponent />

  return (
    <div className="form-wrapper">
      <div>
        <Field
          label="Name"
          required
          validationState={errors.name ? 'error' : 'none'}
          validationMessage={errors.name?.message}
        >
          <Input
            {...register('name', {
              required: 'Name is required',
              maxLength: 100,
            })}
          />
        </Field>

        <Field
          label="Description"
          validationState={errors.description ? 'error' : 'none'}
          validationMessage={errors.description?.message}
        >
          <Input {...register('description', { maxLength: 200 })} />
        </Field>

        <Field
          required
          label="Application"
          validationState={errors.applicationId ? 'error' : 'none'}
          validationMessage={errors.applicationId?.message}
        >
          {props.selectedTeam ? (
            // Display as read-only during edit
            <Input value={props.selectedTeam?.application ?? 'Application'} readOnly />
          ) : (
            <Select
              {...register('applicationId', {
                required: 'Application is required',
                valueAsNumber: true, // Important to make it a number!
              })}
            >
              <option key={'0'} value={0}>
                -- Select an application --
              </option>
              {applications?.map((o: MessagesApplicationsApplication) => (
                <option key={o.applicationId} value={o.applicationId}>
                  {o.appName}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      <div className="form-action-container">
        <Button disabled={isUpdatingTeam || isAddingTeam} text="Submit" onClick={handleSubmit(onSubmit)} />
      </div>
    </div>
  )
}
